import { useCallback, useMemo } from 'react';

import { Locale, useI18n, usePageContext } from '../../utils';

// Only return start & end time now that OneLink is handling the rest
const useFormatTimeRange = (): ((start: string, end: string) => string) => {
  return useMemo(() => {
    return (start, end) => {
      // Parse start and end times into Date objects
      const startDate = new Date(`01/01/2000 ${start}`);
      const endDate = new Date(`01/01/2000 ${end}`);

      // Format times using built-in Date methods
      const startString = startDate.toLocaleTimeString([], {
        timeStyle: 'short',
      });
      let endString = endDate.toLocaleTimeString([], { timeStyle: 'short' });

      if (endString === '12:00 AM') {
        endString = 'midnight';
      }

      // Return formatted time range
      return `${startString.toLowerCase()} - ${endString.toLowerCase()}`;
    };
  }, []);
};

const Weekday = {
  Monday: new Date('2020-01-06T00:00:00.000Z'),
  Tuesday: new Date('2020-01-07T00:00:00.000Z'),
  Wednesday: new Date('2020-01-08T00:00:00.000Z'),
  Thursday: new Date('2020-01-09T00:00:00.000Z'),
  Friday: new Date('2020-01-10T00:00:00.000Z'),
  Saturday: new Date('2020-01-11T00:00:00.000Z'),
  Sunday: new Date('2020-01-12T00:00:00.000Z'),
} as const;

type Weekday = keyof typeof Weekday;

const useFormatWeekdayRange = (): ((
  start: Weekday,
  end: Weekday,
) => string) => {
  const { locale = Locale.EN_US } = usePageContext<{ locale: Locale }>();

  return useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'long' });

    return (start, end) => formatter.formatRange(Weekday[start], Weekday[end]);
  }, [locale]);
};

const WEEKDAY_ORDER: Weekday[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

type SanityHourDataProps = {
  holidayHours?: ReadonlyArray<
    Pick<Queries.SanityDayAndTime, 'day' | 'opensAt' | 'closesAt'> | undefined
  >;
  hours?: ReadonlyArray<
    Pick<Queries.SanityDayAndTime, 'day' | 'opensAt' | 'closesAt'> | undefined
  >;
  hourText?: string;
  useHolidayHours?: boolean;
};

export const useFormatSanityHourData = (): ((
  sanityHourData: SanityHourDataProps,
) => string[]) => {
  const formatTimeRange = useFormatTimeRange();
  const formatWeekdayRange = useFormatWeekdayRange();

  return useCallback((sanityHourData) => {
    /**
     * This replaces the values for hour.day to read as
     * the en-US version of Weekdays. This is needed to
     * correctly run this hook when Transperfect/Onelink
     * translates our pagedata.json files
     * */
    if (sanityHourData && sanityHourData.hours) {
      sanityHourData.hours.map((hour, index) => {
        if (hour) {
          return ((hour.day as Weekday) = WEEKDAY_ORDER[index]);
        }
      });
    }

    const ranges =
      (sanityHourData.useHolidayHours
        ? sanityHourData.holidayHours
        : sanityHourData.hours) ?? sanityHourData.hours;

    if (!ranges?.length) {
      return sanityHourData.hourText ? [sanityHourData.hourText] : [];
    }

    const combinedRanges = WEEKDAY_ORDER.map(
      (weekday) =>
        ranges.find((range) => range?.day === weekday) ?? {
          closesAt: null,
          day: weekday,
          opensAt: null,
        },
    ).reduce(
      (combinedRanges, range_) => {
        const range = range_ as {
          closesAt: string | null;
          day: Weekday;
          opensAt: string | null;
        };

        const existingCombinedRange = combinedRanges.find(
          (combinedRange) =>
            WEEKDAY_ORDER.indexOf(combinedRange.endDay) ===
              WEEKDAY_ORDER.indexOf(range.day) - 1 &&
            combinedRange.startTime === range.opensAt &&
            combinedRange.endTime === range.closesAt,
        );

        if (existingCombinedRange) {
          existingCombinedRange.endDay = range.day;
        } else {
          combinedRanges.push({
            endDay: range.day,
            endTime: range.closesAt,
            startDay: range.day,
            startTime: range.opensAt,
          });
        }

        return combinedRanges;
      },
      [] as {
        endDay: Weekday;
        endTime: string | null;
        startDay: Weekday;
        startTime: string | null;
      }[],
    );

    let formattedCombinedRanges: string[];

    if (combinedRanges.length === 1) {
      if (
        combinedRanges[0].startTime === null ||
        combinedRanges[0].endTime === null
      ) {
        formattedCombinedRanges = ['closed'];
      } else {
        formattedCombinedRanges = [
          `Daily from ${formatTimeRange(
            combinedRanges[0].startTime,
            combinedRanges[0].endTime,
          )}`,
        ];
      }
    } else {
      formattedCombinedRanges = combinedRanges.map((combinedRange) => {
        const formattedWeekdayRange = formatWeekdayRange(
          combinedRange.startDay,
          combinedRange.endDay,
        );

        const formattedTimeRange =
          combinedRange.startTime === null || combinedRange.endTime === null
            ? 'closed'
            : `${formatTimeRange(
                combinedRange.startTime,
                combinedRange.endTime,
              )}`;

        return `${formattedWeekdayRange}: ${formattedTimeRange}`;
      });
    }

    if (sanityHourData.hourText) {
      formattedCombinedRanges.push(sanityHourData.hourText);
    }

    return formattedCombinedRanges;
  }, []);
};
